/**
 * Canada Malting Co. — Calgary, Alberta, and one of the oldest maltsters in
 * North America.
 *
 * The maker publishes a colour **band** in SRM, plus extract, maximum moisture
 * and total protein, on a page per malt. Each entry takes the mid-point of the
 * colour band; where a °L band is printed alongside the SRM one, the °L figure
 * is the maker's own rather than a conversion.
 *
 * ## Every row here is `representative`, for one missing figure
 *
 * Canada Malting publishes no diastatic power anywhere in the range. A base
 * malt that cannot state one is a base malt this catalog cannot honestly
 * describe, so each entry carries the median diastatic power this package
 * already holds for its class and is marked accordingly. Colour, extract,
 * moisture and protein are all the maker's.
 *
 * The extract figures are the maker's own without a stated grind, and are read
 * here as fine-grind dry basis, which is what the rest of this catalog stores.
 *
 * @see https://canadamalting.com/malts/
 */

import type { Fermentable } from '../../schema/fermentable';

/** Canada Malting Co. fermentables. @see Fermentable */
export const canadaMaltingFermentables: readonly Fermentable[] = [
  {
    id: 'cmc-superior-pilsen',
    name: 'Superior Pilsen Malt',
    maker: 'Canada Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Hand-selected Copeland barley, malted pale and modified hard so it converts easily and gives up its extract. The palest base in the range, and the one to build a helles on.',
    tags: ['base', 'lager', 'pilsner', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'cmc-2-row',
    name: 'CMC 2-Row Malt',
    maker: 'Canada Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The North American workhorse: high extract, excellent fermentability and no strong opinion about the beer it ends up in. A neutral base under anything hop-forward.',
    tags: ['base', 'two-row', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'cmc-superior-pale-ale',
    name: 'Superior Pale Ale Malt',
    maker: 'Canada Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Well modified and kilned a shade darker than the standard two-row, which brings biscuit and bread crust without the depth of a Vienna. A base malt with something to say.',
    tags: ['base', 'pale-ale', 'biscuit', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'cmc-munich',
    name: 'Munich Malt',
    maker: 'Canada Malting',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 11,
    colorEbc: 28,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed in the traditional European way and then kilned hot, which is where the bread-crust aroma comes from. Color and malt depth for amber and dark lagers.',
    tags: ['munich', 'malty', 'lager', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'cmc-white-wheat',
    name: 'White Wheat Malt',
    maker: 'Canada Malting',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Soft white wheat, malted. The highest extract in the range, and the usual way to add head retention and a fuller mouthfeel to almost any style.',
    tags: ['wheat', 'head-retention', 'mouthfeel', 'canadian'],
    provenance: 'representative',
  },
];
